import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Typography,
} from "antd";
import {
  BellOutlined,
  SendOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useGetPushCampaignAudienceSummary,
  useSearchPushCampaignRecipients,
  useSendPushCampaign,
} from "../services/push-campaign.service";

const { Title, Text } = Typography;
const { TextArea } = Input;

type RecipientSegment = "all" | "users" | "attendants" | "single";

type PushRecord = {
  key: string;
  name: string;
  segment: string;
  sentAt: string;
  delivered: number;
  failed: number;
  status: string;
};

const audienceFilterOptions = [
  { value: "incomplete-signup", label: "Incomplete Signup (Drafts)" },
  { value: "incomplete-attendant-application", label: "Incomplete Attendant Application" },
  { value: "new-today", label: "New Users - Today" },
  { value: "new-this-week", label: "New Users - This Week" },
  { value: "new-last-week", label: "New Users - Last Week" },
  { value: "inactive-attendants", label: "Inactive Attendants" },
  { value: "deactivated-users", label: "Deactivated / Terminated Accounts" },
];

const PushNotificationDashboard = () => {
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const [recipientSegment, setRecipientSegment] = useState<RecipientSegment>("all");
  const [pushes, setPushes] = useState<PushRecord[]>([]);
  const [audienceSummary, setAudienceSummary] = useState({ usersCount: 0, attendantsCount: 0, totalCount: 0 });
  const [recipientOptions, setRecipientOptions] = useState<{ value: string; label: string }[]>([]);
  const [testRecipientOptions, setTestRecipientOptions] = useState<{ value: string; label: string }[]>([]);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const testSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const watchedTitle = Form.useWatch("title", form) ?? "";
  const watchedBody = Form.useWatch("body", form) ?? "";
  const watchedCampaignName = Form.useWatch("campaignName", form) ?? "";
  const watchedSingleRecipientUserId = Form.useWatch("singleRecipientUserId", form) ?? "";

  const audienceSummaryMutation = useGetPushCampaignAudienceSummary({
    onSuccess: (response: any) => {
      if (response?.statusCode === 200 && response?.data) {
        setAudienceSummary({
          usersCount: response.data.usersCount ?? 0,
          attendantsCount: response.data.attendantsCount ?? 0,
          totalCount: response.data.totalCount ?? 0,
        });
      }
    },
    onError: () => {
      message.error("Failed to load audience counts.");
    },
  });

  useEffect(() => {
    audienceSummaryMutation.mutate();
  }, []);

  const searchRecipientsMutation = useSearchPushCampaignRecipients({
    onSuccess: (response: any) => {
      if (response?.statusCode === 200 && Array.isArray(response?.data)) {
        setRecipientOptions(
          response.data.map((r: any) => ({
            value: r.userId,
            label: `${r.fullName || r.email} (${r.roleName}) - ${r.email}`,
          }))
        );
      }
    },
  });

  const testSearchRecipientsMutation = useSearchPushCampaignRecipients({
    onSuccess: (response: any) => {
      if (response?.statusCode === 200 && Array.isArray(response?.data)) {
        setTestRecipientOptions(
          response.data.map((r: any) => ({
            value: r.userId,
            label: `${r.fullName || r.email} (${r.roleName}) - ${r.email}`,
          }))
        );
      }
    },
  });

  const onSearchRecipients = (value: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value || value.trim().length < 2) {
      setRecipientOptions([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchRecipientsMutation.mutate(value.trim());
    }, 300);
  };

  const onSearchTestRecipients = (value: string) => {
    if (testSearchTimeoutRef.current) clearTimeout(testSearchTimeoutRef.current);
    if (!value || value.trim().length < 2) {
      setTestRecipientOptions([]);
      return;
    }
    testSearchTimeoutRef.current = setTimeout(() => {
      testSearchRecipientsMutation.mutate(value.trim());
    }, 300);
  };

  const sendPushMutation = useSendPushCampaign({
    onSuccess: (response: any) => {
      if (response?.statusCode === 200) {
        const sentCount = response?.data?.sentCount ?? 0;
        const failedRecipients: string[] = response?.data?.failedRecipients ?? [];
        setPushes((prev) => [
          {
            key: `${Date.now()}`,
            name: (response?.data?.campaignName ?? watchedCampaignName) || "Push Notification",
            segment: getSegmentLabel(recipientSegment),
            sentAt: dayjs().format("YYYY-MM-DD HH:mm"),
            delivered: sentCount,
            failed: failedRecipients.length,
            status: failedRecipients.length > 0 ? "Partially Failed" : "Queued",
          },
          ...prev,
        ]);

        message.success(`Push notification queued for ${sentCount} recipients.`);
      } else {
        message.error(response?.statusMessage ?? "Failed to send push notification.");
      }
    },
    onError: (error: any) => {
      message.error(error?.message ?? "Failed to send push notification.");
    },
  });

  const onSendCampaign = async () => {
    try {
      const values = await form.validateFields();

      sendPushMutation.mutate({
        campaignName: values.campaignName,
        recipientSegment,
        title: values.title,
        body: values.body,
        audienceFilters: recipientSegment === "single" ? undefined : values.audienceFilters,
        singleRecipientUserId: recipientSegment === "single" ? values.singleRecipientUserId : undefined,
      });
    } catch {
      // Form validation errors are already surfaced by Ant Design.
    }
  };

  const onSendTest = async () => {
    try {
      const mainValues = await form.validateFields(["title", "body"]);
      const testValues = await testForm.validateFields();

      if (!testValues.testRecipientUserId) {
        message.error("Select a test recipient.");
        return;
      }

      sendPushMutation.mutate({
        campaignName: watchedCampaignName || "Test Push",
        recipientSegment,
        title: mainValues.title,
        body: mainValues.body,
        testRecipientUserId: testValues.testRecipientUserId,
      });
    } catch {
      // Form validation errors are already surfaced by Ant Design.
    }
  };

  const estimatedReach = useMemo(() => {
    if (recipientSegment === "single") return watchedSingleRecipientUserId ? 1 : 0;
    if (recipientSegment === "users") return audienceSummary.usersCount;
    if (recipientSegment === "attendants") return audienceSummary.attendantsCount;
    return audienceSummary.totalCount;
  }, [recipientSegment, audienceSummary, watchedSingleRecipientUserId]);

  const deliveryReadiness = useMemo(() => {
    const checks = [
      Boolean(watchedCampaignName.trim()),
      Boolean(watchedTitle.trim()),
      Boolean(watchedBody.trim()),
      estimatedReach > 0,
    ];
    const completed = checks.filter(Boolean).length;
    return Math.round((completed / checks.length) * 100);
  }, [watchedCampaignName, watchedTitle, watchedBody, estimatedReach]);

  const getDeliveryRateDisplay = (delivered: number, failed: number) => {
    const total = delivered + failed;
    if (total <= 0) return "-";
    return `${Math.round((delivered / total) * 100)}%`;
  };

  const columns = [
    {
      title: "Push",
      dataIndex: "name",
      key: "name",
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: "Audience",
      dataIndex: "segment",
      key: "segment",
    },
    {
      title: "Sent",
      dataIndex: "sentAt",
      key: "sentAt",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: string) => <span>{value}</span>,
    },
    {
      title: "Delivery Rate",
      key: "deliveryRate",
      render: (_: unknown, row: any) => <Text>{getDeliveryRateDisplay(row.delivered, row.failed)}</Text>,
    },
  ];

  function getSegmentLabel(segment: RecipientSegment): string {
    if (segment === "users") return "Users";
    if (segment === "attendants") return "Attendants";
    if (segment === "single") return "Single User";
    return "Users + Attendants";
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            Push Notifications
          </Title>
          <Text type="secondary">
            Send push notifications to travelers and attendants on mobile.
          </Text>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Estimated Reach"
              value={estimatedReach}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Registered Users w/ Device" value={audienceSummary.usersCount} prefix={<UserOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Registered Attendants w/ Device" value={audienceSummary.attendantsCount} prefix={<BellOutlined />} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Compose Push Notification">
            <Form form={form} layout="vertical">
              <Form.Item label="Campaign Name" name="campaignName" rules={[{ required: true, message: "Campaign name is required." }]}>
                <Input placeholder="Example: Weekend Trip Reminder" />
              </Form.Item>

              <Form.Item label="Audience" required>
                <Radio.Group
                  value={recipientSegment}
                  onChange={(e) => setRecipientSegment(e.target.value)}
                >
                  <Space direction="vertical">
                    <Radio value="all">All Users and Attendants</Radio>
                    <Radio value="users">Users Only</Radio>
                    <Radio value="attendants">Attendants Only</Radio>
                    <Radio value="single">Single User</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>

              {recipientSegment === "single" ? (
                <Form.Item
                  label="Recipient"
                  name="singleRecipientUserId"
                  rules={[{ required: true, message: "Select a recipient." }]}
                >
                  <Select
                    showSearch
                    placeholder="Search by name or email"
                    filterOption={false}
                    onSearch={onSearchRecipients}
                    loading={searchRecipientsMutation.isPending}
                    options={recipientOptions}
                    notFoundContent={searchRecipientsMutation.isPending ? "Searching..." : "Type at least 2 characters"}
                  />
                </Form.Item>
              ) : (
                <Form.Item label="Audience Filters" name="audienceFilters">
                  <Select
                    mode="multiple"
                    options={audienceFilterOptions}
                    placeholder="Filter by segments"
                    allowClear
                  />
                </Form.Item>
              )}

              <Form.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: "Title is required." }]}
                extra="Shown as the notification's bold headline on the device."
              >
                <Input placeholder="Your Trip Starts Soon!" maxLength={65} showCount />
              </Form.Item>

              <Form.Item
                label="Body"
                name="body"
                rules={[{ required: true, message: "Body is required." }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Write your push notification message..."
                  maxLength={240}
                  showCount
                />
              </Form.Item>

              <Space>
                <Button type="primary" icon={<SendOutlined />} onClick={onSendCampaign} loading={sendPushMutation.isPending}>
                  Send Push
                </Button>
              </Space>
            </Form>
          </Card>

          <Card title="Send Test Push" style={{ marginTop: 16 }}>
            <Form form={testForm} layout="inline">
              <Form.Item
                label="Test Recipient"
                name="testRecipientUserId"
                style={{ minWidth: 320 }}
              >
                <Select
                  showSearch
                  placeholder="Search by name or email"
                  filterOption={false}
                  onSearch={onSearchTestRecipients}
                  loading={testSearchRecipientsMutation.isPending}
                  options={testRecipientOptions}
                  notFoundContent={testSearchRecipientsMutation.isPending ? "Searching..." : "Type at least 2 characters"}
                />
              </Form.Item>
              <Form.Item>
                <Button icon={<SendOutlined />} onClick={onSendTest} loading={sendPushMutation.isPending}>
                  Send Test
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Live Preview">
            <Card size="small" style={{ background: "#fafafa" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <BellOutlined style={{ fontSize: 20, marginTop: 2, color: "#0F1B84" }} />
                <div style={{ flex: 1 }}>
                  <Text strong>{watchedTitle.trim() || "Notification title"}</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">{watchedBody.trim() || "Notification body will preview here"}</Text>
                  </div>
                </div>
              </div>
            </Card>

            <div style={{ marginTop: 16 }}>
              <Text strong>Delivery Readiness</Text>
              <Progress percent={deliveryReadiness} strokeColor="#16a34a" />
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="Recent Push Notifications" style={{ marginTop: 16 }}>
        <Tabs
          items={[
            {
              key: "all",
              label: "All",
              children: <Table columns={columns} dataSource={pushes} pagination={false} />,
            },
            {
              key: "users",
              label: "Users",
              children: (
                <Table
                  columns={columns}
                  dataSource={pushes.filter((x) => x.segment.includes("User"))}
                  pagination={false}
                />
              ),
            },
            {
              key: "attendants",
              label: "Attendants",
              children: (
                <Table
                  columns={columns}
                  dataSource={pushes.filter((x) => x.segment.includes("Attendant"))}
                  pagination={false}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default PushNotificationDashboard;
